<template>
  <v-container fluid class="mt-16 px-6">
    <div>
      <v-card flat>
        <v-card-title class="d-flex align-center">
          <h2>Role Restrictions</h2>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="refresh" :loading="loading">
            <v-icon class="mr-2">mdi-refresh</v-icon>Refresh
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Hat selector + management -->
          <v-sheet class="pa-3 mb-4" color="grey lighten-4" rounded>
            <div class="d-flex align-center flex-wrap">
              <v-autocomplete
                v-model="selectedHatId"
                :items="hats"
                item-text="name"
                item-value="id"
                label="Hat"
                prepend-icon="mdi-hard-hat"
                dense
                hide-details
                style="max-width: 380px"
                class="mr-3"
              ></v-autocomplete>
              <v-btn
                small
                color="success"
                text
                @click="openHatDialog('create')"
              >
                <v-icon small class="mr-1">mdi-plus</v-icon>New Hat
              </v-btn>
              <v-btn
                small
                color="primary"
                text
                :disabled="!selectedHat"
                @click="openHatDialog('edit')"
              >
                <v-icon small class="mr-1">mdi-pencil</v-icon>Edit Details
              </v-btn>
              <v-btn
                small
                color="red"
                text
                :disabled="!selectedHat"
                @click="removeHat"
              >
                <v-icon small class="mr-1">mdi-delete</v-icon>Delete Hat
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                small
                color="primary"
                text
                :disabled="!selectedHat"
                @click="grantAll"
              >
                <v-icon small class="mr-1">mdi-check-all</v-icon>Grant All
              </v-btn>
              <v-btn
                small
                color="grey"
                text
                :disabled="!selectedHat || !localGranted.length"
                @click="removeAll"
              >
                <v-icon small class="mr-1">mdi-close-box-multiple</v-icon>Remove
                All
              </v-btn>
            </div>
            <div v-if="selectedHat" class="caption grey--text mt-2">
              {{ selectedHat.description || "No description." }}
              <v-chip v-if="selectedHat.branch_scope" x-small class="ml-2">
                {{ selectedHat.branch_scope }}
              </v-chip>
              <v-chip x-small class="ml-2">
                {{ (selectedHat.user_ids || []).length }} user(s) assigned
              </v-chip>
            </div>
            <!-- Live summary of what this hat grants (reflects unsaved edits) -->
            <div v-if="selectedHat" class="mt-2">
              <span class="caption grey--text">
                Grants {{ localGranted.length }} permission(s){{
                  isModified ? " — unsaved changes" : ""
                }}:
              </span>
              <span
                v-if="!localGranted.length"
                class="caption red--text font-weight-medium"
              >
                none — holders of this hat see nothing.
              </span>
              <v-chip
                v-for="cat in grantedCategories"
                :key="cat.name"
                x-small
                class="ml-1 mt-1"
                :color="selectedCategory === cat.name ? 'primary' : ''"
                :text-color="selectedCategory === cat.name ? 'white' : ''"
                @click="selectedCategory = cat.name"
              >
                {{ cat.name }} ({{ cat.count }})
              </v-chip>
            </div>
            <div class="caption grey--text mt-1">
              A user's effective permissions are the union of every hat assigned
              to their account. Accounts without hats get NO access — hats are
              the only source of permissions.
            </div>
            <div class="caption red--text text--darken-1 mt-1">
              <v-icon x-small color="red darken-1" class="mr-1"
                >mdi-cancel</v-icon
              >The <strong>Disabled (All)</strong> switch is a global
              kill-switch: turning it on hides that feature for EVERY account
              (including admins) regardless of their hats — no need to uncheck it
              per hat. Changes apply instantly.
            </div>
          </v-sheet>

          <v-text-field
            v-model="search"
            label="Search permissions..."
            prepend-icon="mdi-magnify"
            class="mb-4"
            single-line
            hide-details
            clearable
          ></v-text-field>

          <!-- Category filter chips -->
          <div class="mb-4">
            <v-chip
              v-for="cat in categories"
              :key="cat"
              :color="selectedCategory === cat ? 'primary' : 'grey lighten-2'"
              :text-color="selectedCategory === cat ? 'white' : 'black'"
              class="mr-2 mb-2"
              @click="selectedCategory = selectedCategory === cat ? null : cat"
              small
            >
              {{ cat }}
              <v-badge
                :content="getCategoryCount(cat).toString()"
                inline
                class="ml-1"
              ></v-badge>
            </v-chip>
          </div>

          <!-- Permissions table -->
          <v-data-table
            :items="filteredPermissions"
            :headers="headers"
            :search="search"
            :items-per-page="15"
            :loading="loading"
            group-by="category"
            show-group-by
            class="elevation-1"
          >
            <!-- Category group header -->
            <template v-slot:group.header="{ group, toggle, isOpen }">
              <td :colspan="headers.length" class="pa-2">
                <v-btn icon small @click="toggle">
                  <v-icon>{{
                    isOpen ? "mdi-chevron-up" : "mdi-chevron-down"
                  }}</v-icon>
                </v-btn>
                <span class="font-weight-bold text-uppercase">{{ group }}</span>
                <v-chip x-small class="ml-2">{{
                  getCategoryCount(group)
                }}</v-chip>
              </td>
            </template>

            <!-- Permission name -->
            <template v-slot:item.permission_name="{ item }">
              <div>
                <span class="font-weight-medium">{{
                  item.permission_name
                }}</span>
                <br />
                <span class="caption grey--text">{{ item.description }}</span>
              </div>
            </template>

            <!-- Permission key -->
            <template v-slot:item.permission_key="{ item }">
              <code class="caption">{{ item.permission_key }}</code>
            </template>

            <!-- Granted checkbox for the selected hat -->
            <template v-slot:item.granted="{ item }">
              <v-checkbox
                :input-value="localGranted.includes(item.id)"
                @change="toggleGranted(item.id, $event)"
                :disabled="!selectedHat || item.is_globally_disabled"
                color="primary"
                hide-details
                dense
                class="mt-0 pt-0 shrink"
              ></v-checkbox>
            </template>

            <!-- Global kill-switch: hides this feature for EVERY account -->
            <template v-slot:item.global_disable="{ item }">
              <v-switch
                :input-value="item.is_globally_disabled"
                @change="toggleGlobalDisable(item, $event)"
                :loading="globalBusy === item.id"
                :disabled="globalBusy === item.id"
                color="red"
                hide-details
                dense
                class="mt-0 pt-0 shrink"
              ></v-switch>
            </template>
          </v-data-table>

          <!-- Save -->
          <div class="mt-4 d-flex justify-end" v-if="isModified">
            <v-chip color="orange" text-color="white" class="mr-3">
              Unsaved changes
            </v-chip>
            <v-btn color="primary" @click="saveBundle" :loading="saving">
              <v-icon class="mr-2">mdi-content-save</v-icon>
              Save Bundle
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Hat create/edit dialog -->
      <v-dialog v-model="hatDialog.show" persistent width="480">
        <v-card class="pa-5">
          <v-card-title>
            <h3>{{ hatDialog.mode === "create" ? "New Hat" : "Edit Hat" }}</h3>
          </v-card-title>
          <v-card-text>
            <v-form ref="hatForm">
              <v-text-field
                label="Name"
                v-model="hatDialog.form.name"
                :rules="[(v) => !!v || 'Name is required']"
                prepend-icon="mdi-hard-hat"
              ></v-text-field>
              <v-textarea
                label="Description"
                v-model="hatDialog.form.description"
                prepend-icon="mdi-text"
                rows="3"
                auto-grow
              ></v-textarea>
              <v-select
                label="Branch Scope"
                v-model="hatDialog.form.branch_scope"
                :items="branchScopes"
                prepend-icon="mdi-domain"
                clearable
              ></v-select>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click="saveHat" :loading="hatDialog.saving">
              {{ hatDialog.mode === "create" ? "Create" : "Save" }}
            </v-btn>
            <v-btn @click="hatDialog.show = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  data() {
    return {
      search: "",
      selectedCategory: null,
      loading: false,
      saving: false,
      selectedHatId: null,
      // id of the permission whose global kill-switch is mid-save
      globalBusy: null,
      // Local copy of the selected hat's permission ids for editing
      localGranted: [],
      headers: [
        { text: "Feature", value: "permission_name", width: "30%" },
        { text: "Key", value: "permission_key", width: "25%" },
        { text: "Category", value: "category", width: "15%" },
        { text: "Granted", value: "granted", sortable: false, width: "10%" },
        {
          text: "Disabled (All)",
          value: "global_disable",
          sortable: false,
          width: "12%",
          groupable: false,
        },
      ],
      branchScopes: [
        { text: "Cantilan", value: "cantilan" },
        { text: "Tandag", value: "tandag" },
        { text: "Both", value: "both" },
      ],
      hatDialog: {
        show: false,
        mode: "create",
        saving: false,
        form: {},
      },
    };
  },
  computed: {
    ...mapGetters({
      permissions: "permission/permissions",
      hats: "permission/hats",
      user: "auth/user",
    }),
    categories() {
      const cats = [...new Set(this.permissions.map((p) => p.category))];
      return cats.sort();
    },
    filteredPermissions() {
      if (!this.selectedCategory) return this.permissions;
      return this.permissions.filter(
        (p) => p.category === this.selectedCategory
      );
    },
    selectedHat() {
      return this.hats.find((h) => h.id === this.selectedHatId) || null;
    },
    // Per-category counts of the permissions this hat currently grants
    // (follows localGranted, so unsaved checkbox edits show immediately).
    grantedCategories() {
      const counts = {};
      this.permissions.forEach((p) => {
        if (this.localGranted.includes(p.id)) {
          counts[p.category] = (counts[p.category] || 0) + 1;
        }
      });
      return Object.keys(counts)
        .sort()
        .map((name) => ({ name, count: counts[name] }));
    },
    isModified() {
      if (!this.selectedHat) return false;
      const original = (this.selectedHat.permission_ids || [])
        .slice()
        .sort((a, b) => a - b)
        .join(",");
      const local = this.localGranted
        .slice()
        .sort((a, b) => a - b)
        .join(",");
      return original !== local;
    },
  },
  watch: {
    selectedHat(hat) {
      this.localGranted = [...((hat && hat.permission_ids) || [])];
    },
  },
  methods: {
    ...mapActions({
      fetch_permissions: "permission/fetch_permissions",
      fetch_hats: "permission/fetch_hats",
      create_hat: "permission/create_hat",
      update_hat: "permission/update_hat",
      delete_hat: "permission/delete_hat",
      set_global_disable: "permission/set_global_disable",
    }),

    // Flip a permission's global kill-switch. Applies immediately (not part of
    // the hat's Save Bundle) since it affects every account at once.
    async toggleGlobalDisable(item, value) {
      const disabled = !!value;
      this.globalBusy = item.id;
      try {
        await this.set_global_disable({ id: item.id, disabled });
        this.$alert({
          title: disabled ? "Feature disabled" : "Feature enabled",
          message: disabled
            ? `"${item.permission_name}" is now hidden for ALL accounts.`
            : `"${item.permission_name}" is available again (where granted by a hat).`,
          color: disabled ? "warning" : "success",
          icon: disabled ? "mdi-cancel" : "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            err.response?.data?.message || "Failed to update the global switch",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.globalBusy = null;
    },

    toggleGranted(id, checked) {
      if (checked) {
        if (!this.localGranted.includes(id)) this.localGranted.push(id);
      } else {
        this.localGranted = this.localGranted.filter((x) => x !== id);
      }
    },

    // Stage every permission for the selected hat; still requires Save Bundle.
    grantAll() {
      this.localGranted = this.permissions.map((p) => p.id);
    },

    // Stage removal of every permission; still requires Save Bundle.
    removeAll() {
      this.localGranted = [];
    },

    getCategoryCount(cat) {
      return this.permissions.filter((p) => p.category === cat).length;
    },

    async saveBundle() {
      if (!this.selectedHat) return;
      this.saving = true;
      try {
        await this.update_hat({
          id: this.selectedHat.id,
          name: this.selectedHat.name,
          description: this.selectedHat.description,
          branch_scope: this.selectedHat.branch_scope,
          permission_ids: this.localGranted,
        });
        this.$alert({
          title: "Success",
          message: `Updated the "${this.selectedHat.name}" bundle`,
          color: "success",
          icon: "mdi-check-circle",
        });
      } catch (err) {
        this.$alert({
          title: "Error",
          message: err.response?.data?.message || "Failed to save hat bundle",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.saving = false;
    },

    openHatDialog(mode) {
      this.hatDialog.mode = mode;
      this.hatDialog.form =
        mode === "create"
          ? { name: "", description: "", branch_scope: null }
          : {
              name: this.selectedHat.name,
              description: this.selectedHat.description,
              branch_scope: this.selectedHat.branch_scope,
            };
      this.hatDialog.show = true;
    },

    async saveHat() {
      if (!this.$refs.hatForm.validate()) return;
      this.hatDialog.saving = true;
      try {
        if (this.hatDialog.mode === "create") {
          const hat = await this.create_hat({
            ...this.hatDialog.form,
            permission_ids: [],
          });
          this.selectedHatId = hat.id;
        } else {
          await this.update_hat({
            id: this.selectedHat.id,
            ...this.hatDialog.form,
            permission_ids: this.localGranted,
          });
        }
        this.hatDialog.show = false;
      } catch (err) {
        this.$alert({
          title: "Error",
          message: err.response?.data?.message || "Failed to save hat",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
      this.hatDialog.saving = false;
    },

    async removeHat() {
      if (!this.selectedHat) return;
      const confirmed = await this.$confirm({
        title: "Delete Hat",
        message: `Delete the "${this.selectedHat.name}" hat? Accounts holding it will lose exactly this bundle of permissions.`,
        color: "red",
        icon: "mdi-delete",
      });
      if (!confirmed) return;
      try {
        await this.delete_hat(this.selectedHat.id);
        this.selectedHatId = this.hats.length ? this.hats[0].id : null;
      } catch (err) {
        this.$alert({
          title: "Error",
          message: err.response?.data?.message || "Failed to delete hat",
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },

    async loadAll() {
      this.loading = true;
      await Promise.all([this.fetch_permissions(), this.fetch_hats()]);
      if (!this.selectedHat && this.hats.length) {
        this.selectedHatId = this.hats[0].id;
      }
      this.loading = false;
    },

    async refresh() {
      await this.loadAll();
    },
  },
  async created() {
    await this.loadAll();
  },
};
</script>

<style scoped>
.shrink {
  flex: 0 0 auto;
}
</style>
